import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import swal from "sweetalert";
import { cambiarEstadoIniciativa, habilitarIniciativa } from "../../redux";
import { getIniciativaErrorCode } from "../../services/iniciativasIA";
import { handleSwalAwareEscapeKeyDown } from "./swalEscapeGuard";

// Modal de acciones del flujo (spec 008, Incrementos 2-3, RF-37/RF-38/RF-39/RF-40/RF-46/RF-55,
// plan.md §14.3 — T-APP-19).
//
// Máquina de pasos: "opciones" -> ("enviar" | "habilitar" | "devolver") -> swal de confirmación con la
// consecuencia concreta (RF-21/RF-38, dos salidas explícitas). "Cancelar envío" NO tiene paso intermedio
// (RF-35): confirma directo desde "opciones". Cada paso intermedio tiene un enlace "Volver" que regresa a
// "opciones" SIN ejecutar nada (CA-31) y el botón de acción permanece deshabilitado hasta que el dato esté
// completo (CA-31/CA-35/CA-42). El modal siempre muestra el nombre de la iniciativa y su estado actual
// (RF-37), y cada botón usa la clase `.estado-*` del estado que produce.
//
// T-SPIKE-1 (RESUELTO, tasks.md): este modal encadena swal() en TODAS sus rutas -> enforceFocus={false}
// obligatorio.
//
// ⛔ P5-bis: el paso "habilitar" lleva SOLO un textarea de comentario -- ningún desplegable de prioridad.
//
// La variante "champion" (Habilitar/Devolver) la ofrece el padre (IniciativasTable, T-APP-22) solo cuando
// row.IdChampion === IdUsuario -- comodidad visual (P17); el 403 real lo pone el SP (RF-55). Este modal no
// repite ese chequeo: confía en la autoridad del backend y solo mapea NO_AUTORIZADO a un mensaje genérico
// (S-11-bis: no distingue "no eres el dueño" de "no eres el Champion asignado").
const ESTADOS_ENVIABLES = ["GUARDADO", "DEVUELTA", "CANCELADA"];

const IniciativaAccionesModal = ({ show, onHide, row, variante, champions }) => {
  const [t] = useTranslation("iniciativasIA");
  const dispatch = useDispatch();
  const history = useHistory();

  const [paso, setPaso] = useState("opciones");
  const [idChampionElegido, setIdChampionElegido] = useState("");
  const [comentario, setComentario] = useState("");
  const [ejecutando, setEjecutando] = useState(false);

  useEffect(() => {
    if (show) {
      setPaso("opciones");
      setIdChampionElegido("");
      setComentario("");
    }
  }, [show, row?.IdIniciativa]);

  if (!row) return null;

  const codigoEstado = row.CodigoEstado;
  const puedeEnviar = variante === "gestion" && ESTADOS_ENVIABLES.includes(codigoEstado);
  const puedeCancelar = variante === "gestion" && codigoEstado === "ENVIADO";
  const puedeEvaluar = variante === "champion" && codigoEstado === "ENVIADO";

  const volver = () => setPaso("opciones");

  // RF-21/RF-38: swal que enuncia la consecuencia concreta antes de ejecutar; `onOk` arma el swal final
  // propio de cada transición (éxito simple o el de RF-46 para habilitar).
  const ejecutar = (thunkAction, payload, textoConfirmacion, onOk) => {
    swal({
      title: t("iniciativasIA_acciones_swal_confirm_title"),
      text: textoConfirmacion,
      icon: "info",
      buttons: [t("iniciativasIA_swal_btn_no"), t("iniciativasIA_swal_btn_confirm")],
    }).then((confirmado) => {
      if (!confirmado) return;
      setEjecutando(true);
      dispatch(thunkAction(row.IdIniciativa, payload))
        .then((data) => {
          setEjecutando(false);
          onOk(data);
        })
        .catch((err) => {
          setEjecutando(false);
          const code = getIniciativaErrorCode(err);
          const text =
            code === "NO_AUTORIZADO"
              ? t("iniciativasIA_error_no_autorizado")
              : code === "TRANSICION_INVALIDA"
              ? t("iniciativasIA_error_transicion_invalida")
              : code === "CHAMPION_REQUERIDO"
              ? t("iniciativasIA_error_champion_requerido")
              : code === "COMENTARIO_REQUERIDO"
              ? t("iniciativasIA_error_comentario_requerido")
              : t("iniciativasIA_swal_error_generic");
          swal(t("iniciativasIA_swal_error_title"), text, "error");
        });
    });
  };

  const confirmarEnviar = () => {
    const champion = (champions || []).find((c) => c.IdUsuario === idChampionElegido);
    ejecutar(
      cambiarEstadoIniciativa,
      { accion: "ENVIAR", IdChampion: idChampionElegido },
      t("iniciativasIA_acciones_swal_enviar_text", {
        champion: champion ? champion.NombreCompleto : "",
      }),
      () => {
        onHide();
        swal({
          title: t("iniciativasIA_acciones_swal_enviado_title"),
          // Sin `text` el título es :last-child y SweetAlert le deja padding-bottom:0 — el aviso
          // se ve cortado contra el borde inferior.
          text: t("iniciativasIA_acciones_swal_enviado_text"),
          icon: "success",
          timer: 1600,
          buttons: false,
        });
      }
    );
  };

  const confirmarCancelar = () => {
    ejecutar(
      cambiarEstadoIniciativa,
      { accion: "CANCELAR_ENVIO" },
      t("iniciativasIA_acciones_swal_cancelar_text"),
      () => {
        onHide();
        swal({
          title: t("iniciativasIA_acciones_swal_cancelado_title"),
          // Sin `text` el título es :last-child y SweetAlert le deja padding-bottom:0 — el aviso
          // se ve cortado contra el borde inferior.
          text: t("iniciativasIA_acciones_swal_cancelado_text"),
          icon: "success",
          timer: 1600,
          buttons: false,
        });
      }
    );
  };

  const confirmarDevolver = () => {
    ejecutar(
      cambiarEstadoIniciativa,
      { accion: "DEVOLVER", Comentario: comentario.trim() },
      t("iniciativasIA_acciones_swal_devolver_text"),
      () => {
        onHide();
        swal({
          title: t("iniciativasIA_acciones_swal_devuelto_title"),
          // Sin `text` el título es :last-child y SweetAlert le deja padding-bottom:0 — el aviso
          // se ve cortado contra el borde inferior.
          text: t("iniciativasIA_acciones_swal_devuelto_text"),
          icon: "success",
          timer: 1600,
          buttons: false,
        });
      }
    );
  };

  // RF-46/CA-45: el SweetAlert final trae código/nombre/gerente en la MISMA respuesta del SP -- sin un
  // segundo GET -- y "Ir a proyecto" navega a /schedule/:IdProyecto, la misma ruta a la que lleva el clic
  // en una fila de views/Projects.jsx y a la que apunta el correo mailProjectCreated. Sin prioridad
  // (P5-bis).
  const confirmarHabilitar = () => {
    ejecutar(
      habilitarIniciativa,
      { Comentario: comentario.trim() },
      t("iniciativasIA_acciones_swal_habilitar_text"),
      (data) => {
        onHide();
        swal({
          title: t("iniciativasIA_acciones_swal_habilitado_title"),
          icon: "success",
          text: t("iniciativasIA_acciones_swal_habilitado_text", {
            codigo: data.CodigoProyecto,
            nombre: data.NombreProyecto,
            gerente: data.NombreGerente,
          }),
          buttons: [
            t("iniciativasIA_swal_btn_understood"),
            t("iniciativasIA_acciones_btn_ir_proyecto"),
          ],
        }).then((irAProyecto) => {
          if (irAProyecto) history.push(`/schedule/${data.IdProyecto}`);
        });
      }
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      onEscapeKeyDown={handleSwalAwareEscapeKeyDown}
      enforceFocus={false}
      centered
    >
      <Modal.Header>
        <Modal.Title>{t("iniciativasIA_acciones_modal_title")}</Modal.Title>
        {/* `.close` de BS4, no `closeButton` de react-bootstrap: ese renderiza `.btn-close` (Bootstrap 5),
            que no existe en el CSS 4.6 que compila la app (DT-3) y queda invisible. */}
        <button
          type="button"
          className="close"
          aria-label={t("iniciativasIA_modal_close_aria")}
          onClick={onHide}
          disabled={ejecutando}
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <div className="font-weight-bold">{row.Nombre}</div>
          <span className={`badge ${row.ClaseCss}`}>{row.NombreEstado}</span>
        </div>

        {paso === "opciones" && (
          <div className="d-flex flex-column">
            {puedeEnviar && (
              <button
                type="button"
                className="btn estado-enviado mb-2"
                onClick={() => setPaso("enviar")}
              >
                <i className="fa fa-paper-plane mr-2"></i>
                {t("iniciativasIA_acciones_btn_enviar")}
              </button>
            )}
            {puedeCancelar && (
              <button
                type="button"
                className="btn estado-cancelada mb-2"
                disabled={ejecutando}
                onClick={confirmarCancelar}
              >
                <i className="fa fa-ban mr-2"></i>
                {t("iniciativasIA_acciones_btn_cancelar")}
              </button>
            )}
            {puedeEvaluar && (
              <>
                <button
                  type="button"
                  className="btn estado-habilitado mb-2"
                  onClick={() => setPaso("habilitar")}
                >
                  <i className="fa fa-check mr-2"></i>
                  {t("iniciativasIA_acciones_btn_habilitar")}
                </button>
                <button
                  type="button"
                  className="btn estado-devuelta mb-2"
                  onClick={() => setPaso("devolver")}
                >
                  <i className="fa fa-undo mr-2"></i>
                  {t("iniciativasIA_acciones_btn_devolver")}
                </button>
              </>
            )}
            {!puedeEnviar && !puedeCancelar && !puedeEvaluar && (
              <p className="text-muted mb-0">{t("iniciativasIA_acciones_sin_opciones")}</p>
            )}
          </div>
        )}

        {paso === "enviar" && (
          <div>
            <label className="brand-required">{t("iniciativasIA_acciones_field_champion")}</label>
            <select
              className="form-control mb-3"
              value={idChampionElegido}
              onChange={(e) => setIdChampionElegido(e.target.value)}
            >
              <option value="">{t("iniciativasIA_select_placeholder")}</option>
              {(champions || []).map((c) => (
                <option key={c.IdUsuario} value={c.IdUsuario}>
                  {c.NombreCompleto}
                </option>
              ))}
            </select>
            <div className="d-flex justify-content-between align-items-center">
              <button type="button" className="btn btn-link p-0" onClick={volver}>
                {t("iniciativasIA_acciones_btn_volver")}
              </button>
              <button
                type="button"
                className="btn estado-enviado"
                disabled={!idChampionElegido || ejecutando}
                onClick={confirmarEnviar}
              >
                {t("iniciativasIA_acciones_btn_enviar")}
              </button>
            </div>
          </div>
        )}

        {paso === "devolver" && (
          <div>
            <label className="brand-required">{t("iniciativasIA_acciones_field_motivo")}</label>
            <textarea
              rows={4}
              className="form-control mb-3"
              placeholder={t("iniciativasIA_acciones_placeholder_motivo")}
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
            ></textarea>
            <div className="d-flex justify-content-between align-items-center">
              <button type="button" className="btn btn-link p-0" onClick={volver}>
                {t("iniciativasIA_acciones_btn_volver")}
              </button>
              <button
                type="button"
                className="btn estado-devuelta"
                disabled={!comentario.trim() || ejecutando}
                onClick={confirmarDevolver}
              >
                {t("iniciativasIA_acciones_btn_devolver")}
              </button>
            </div>
          </div>
        )}

        {paso === "habilitar" && (
          <div>
            <label className="brand-required">
              {t("iniciativasIA_acciones_field_comentario_habilitar")}
            </label>
            <textarea
              rows={4}
              className="form-control mb-3"
              placeholder={t("iniciativasIA_acciones_placeholder_comentario_habilitar")}
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
            ></textarea>
            <div className="d-flex justify-content-between align-items-center">
              <button type="button" className="btn btn-link p-0" onClick={volver}>
                {t("iniciativasIA_acciones_btn_volver")}
              </button>
              <button
                type="button"
                className="btn estado-habilitado"
                disabled={!comentario.trim() || ejecutando}
                onClick={confirmarHabilitar}
              >
                {t("iniciativasIA_acciones_btn_habilitar")}
              </button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default IniciativaAccionesModal;
